// AccountStatuses is used to display status icons for ledger items (accounts/reconciliation_texts)
// It uses [data-object-type][data-object-id][data-starred] attributes to find items that needs status icons
// After fetching it is setting data-reconciled, data-todos, data-checks attributes for filtering purposes
import $ from 'jquery'

import { SfLog } from '@app/adapters/logger'
import SFetch from 'src/utils/sfetch'
import decompressWorkflowProgresses from 'src/application/workflows/utils/workflow_progress_decompressor'
import {
  TEMPLATE_ROW_SELECTOR,
  DATA_FOR_FILTERING_ATTRIBUTE,
} from 'src/application/workflows/constants'
import { renderMissingReconciliationIcon } from 'src/application/workflows/handlers/working-screen/workflow_icons_manager'
import I18n from 'i18n-js'
import {
  addSpinner,
  clearSpinner,
  renderReviewIcon,
  renderProposalIcon,
  renderWorkflowProgressIcons,
} from './workflow_icons_manager'

export default class AccountStatuses {
  constructor(accountList, filterContainer) {
    if (!accountList || !filterContainer) {
      return
    }

    this.currentPeriodCellsSelector =
      'td.current-period[data-object-type][data-object-id]'

    this.starredCellSelector = `tr.starred ${this.currentPeriodCellsSelector}`

    this.accountList = accountList
    this.filterContainer = filterContainer
    this.toggleFilter = this.filterContainer.find(
      '[data-behaviour~="workflow-account-toggle-filter"]'
    )
    this.cellsToDecorate = this.accountList.find(this.starredCellSelector)

    this.addSpinners(this.cellsToDecorate)
    this.attachEvents()

    const workingScreen = $("[data-behaviour='working-screen']")
    this.ledgerId = workingScreen.data('ledger-id')
    this.workflowId = workingScreen.data('workflow-id')
    this.toggleFilter.trigger('STARTED_FETCHING')
    this.firmId = $('#current_firm_id').val()
    this.companyId = $('#current_company_id').val()

    window.Silverfin.Polling.subscribe(
      `/workflow/${this.workflowId}/ledger/${this.ledgerId}/status/${I18n.locale}`,
      this.workflowProgressReceived,
      workingScreen.data('lastId')
    )
    this.fetchReviews()
    this.fetchProposals()
  }

  findAnyMissingReciliationIconsToRender() {
    this.accountList
      .find(`${TEMPLATE_ROW_SELECTOR}.starred`)
      .each((_idx, el) => {
        // Accounts with missing reconcilation icons don't have [data-for-filtering] attribute
        // it's set by this.addWorkflowProgressDataAttributes() only to data returned by
        // a background job, so only persisted account entries in a database
        // Reconcilation icons are only missing for accounts without account entry persisted
        // in a database for a specific ledger, hence we should also find tds which have
        // [data-object-type="account"] attribute

        const itemWithoutIcon = el.querySelector(
          `td:not(${DATA_FOR_FILTERING_ATTRIBUTE})[data-object-type="account"]`
        )
        if (itemWithoutIcon === null) return

        // ensure these accounts are also considered 'starred' when filtering/counting
        itemWithoutIcon.setAttribute('data-for-filtering', '')
        itemWithoutIcon.setAttribute('data-starred', true)

        this.addMissingCellToCellsToDecorate(itemWithoutIcon)
        renderMissingReconciliationIcon(itemWithoutIcon)
      })
  }

  addMissingCellToCellsToDecorate(cell) {
    const arrayOfCellsToDecorate = Array.from(this.cellsToDecorate)

    if (arrayOfCellsToDecorate.indexOf(cell) > 0) return

    this.cellsToDecorate.push(cell)
  }

  fetchReviews() {
    if (!$('#show-wfreview-checkmarks').length) return

    const url = window.Silverfin.Routes.icons_ledger_workflow_review_path(
      window.Silverfin.currentFirmId,
      window.Silverfin.currentCompanyId,
      this.ledgerId,
      this.workflowId
    )

    SFetch.jsonWithAlert(url).then((data) => {
      this.renderReviewIcons(data)
      $(document).trigger('tooltipsRendered')
    })
  }

  renderReviewIcons({ icons = [] } = {}) {
    icons.forEach((item) => {
      const el = this.currentPeriodCell(item.itemType, item.itemId)

      if (el) {
        renderReviewIcon({
          cell: el,
          tooltip: item.tooltip,
          classes: item.classes?.split(' '),
        })

        this.showEmptyAccount(el)
      }
    })
  }

  async fetchProposals() {
    const url =
      window.Silverfin.Routes.reconciliation_proposals_ledger_company_workflow_path(
        window.Silverfin.currentFirmId,
        window.Silverfin.currentCompanyId,
        this.ledgerId,
        this.workflowId
      )

    try {
      const data = await SFetch.json(url)

      this.renderProposalIcons(data)
      $(document).trigger('tooltipsRendered')
    } catch (e) {
      SfLog.info('Failed to load proposals in Workflow workflow screen', {
        extra: {
          ...window.Silverfin.jsonParams,
          error: e,
        },
      })
    }
  }

  renderProposalIcons(icons) {
    icons.forEach((item) => {
      const el = this.currentPeriodCell(item.ownerType, item.ownerId)

      let tooltip = I18n.t(
        'reconciliation_text.proposal_banner.user_proposed_changes'
      )

      if (item.ownerType === 'reconciliation_text') {
        tooltip += I18n.t(
          'reconciliation_text.proposal_banner.this_reconciliation_template'
        ).toLowerCase()
      } else {
        tooltip += I18n.t('account.number').toLowerCase()
      }

      renderProposalIcon({ cell: el, tooltip })
      this.showEmptyAccount(el)
    })
  }

  addSpinners(cells) {
    cells.attr({
      'data-checks': '',
      'data-todos': '',
      'data-reconciled': '',
    })

    cells.each((_idx, cell) => {
      addSpinner(cell)
    })
  }

  clearSpinners() {
    this.cellsToDecorate.each((_idx, cell) => {
      clearSpinner(cell)
    })
  }

  attachEvents() {
    this.toggleFilter.on('STAR_TOGGLE', (_, finder) => {
      const cellsToDecorate = $(finder)
        .closest('[data-behaviour="account-list-item"]')
        .find(this.currentPeriodCellsSelector)

      if (cellsToDecorate.length > 0) {
        this.addSpinners(cellsToDecorate)
      }
      // we need to reset this attribute, and let it be set by the WF progress handling, if still applicable.
      cellsToDecorate.removeAttr('data-for-filtering')

      this.requestWorkflowProgress()
    })
  }

  workflowProgressReceived = (data) => {
    // eslint-disable-line no-undef
    const { statuses, progress, error } = data

    // Process template statuses (partial response or cached full response)
    if (statuses) {
      const mappedStatuses = decompressWorkflowProgresses(statuses)

      mappedStatuses.forEach((obj) => {
        const cell = this.currentPeriodCell(obj.object_type, obj.object_id)

        if (cell) {
          renderWorkflowProgressIcons({ cell, apiObj: obj })
          this.addWorkflowProgressDataAttributes(cell, obj)
          this.showEmptyAccountWithActivity(cell, obj)
        }
      })

      // https://gitlab.silverfin.com/development/silverfin/-/issues/22651
      // When in a ledger we don't have an account entry record but account of that account entry is starred
      // we're not able to render any reconciliation icon, due to a missing record in a database.
      // Hence, we need to render that icon on the front-end, more here:
      // https://silverfin.quip.com/VTHAAybIJzdf/Missing-reconciliation-icons-discovery-and-summary
      this.findAnyMissingReciliationIconsToRender()
      $(document).trigger('tooltipsRendered')
    }

    if (progress) {
      this.toggleFilter.trigger('FINISHED_FETCHING')
      this.clearSpinners()
    }

    // Clear spinners on WF status job error
    if (error) {
      this.clearSpinners()
    }
  }

  // Sends a request to start the WF progress job (which we are listening to via MessageBus)
  requestWorkflowProgress() {
    this.toggleFilter.trigger('STARTED_FETCHING')
    const statusUrl = Silverfin.Routes.status_ledger_company_workflow_path(
      this.firmId,
      this.companyId,
      this.ledgerId,
      this.workflowId
    )
    $.get({ url: statusUrl })
  }

  addWorkflowProgressDataAttributes = (cell, apiObj) => {
    cell.setAttribute('data-checks', apiObj.has_checks)
    cell.setAttribute('data-todos', apiObj.has_todos)
    cell.setAttribute(
      'data-reconciled',
      apiObj.starred &&
        (apiObj.reconciliation_not_necessary_and_no_required_inputs ||
          apiObj.reconciled)
    )
    cell.setAttribute('data-starred', apiObj.starred)
    cell.setAttribute('data-for-filtering', '')
  }

  showEmptyAccountWithActivity = (cell, apiObj) => {
    // Empty accounts should not be visible on the Working Screen.
    // Initially those accounts are hidden on the view level by adding a .hidden-empty-account class.
    // However, we still want to show empty accounts that have stars/todos/notes/check.
    // This data is loaded from the Workflow Progress calculation and the .hidden-empty-account class
    // is removed for accounts that should still be visible.
    if (
      apiObj.has_checks ||
      apiObj.has_todos ||
      apiObj.starred ||
      apiObj.has_notes
    ) {
      this.showEmptyAccount(cell)
    }
  }

  showEmptyAccount = (cell) => {
    cell
      .closest('tr.hidden-empty-account')
      ?.classList.remove('hidden-empty-account')
  }

  currentPeriodCell = (itemType, itemId) => {
    return document.querySelector(
      `td.current-period[data-object-type='${itemType}'][data-object-id='${itemId}']`
    )
  }
}
